import React, { useState } from "react";
import {
  BlueBirdTheme, Button, Checkbox, CrossIcon, Typography,
} from "@kitbird/core";
import styled from "styled-components";
import { addDays, differenceInDays } from "date-fns";

import useCloseReceipt from "./useCloseReceipt";
import { CardContent, PageCard, PageWrapper } from "../../../components/Content/Content.style";
import { HeaderText } from "../../../components/FloatHeader/FloatHeader.style";
import PageLoading from "../../../components/Loading/PageLoading";
import { ReactComponent as PaperListIcon } from "../../../assets/icons/paper_list.svg";
import RedReceiptTable from "./components/RedReceiptTable";
import WhiteReceiptContent from "./components/WhiteReceiptContent";
import FilterDropdown from "../../../components/FilterDropdown";
import ModalReceiptDetail from "./components/ModalReceiptDetail";
import ModalRedReceiptDetail from "./components/ModalRedReceiptDetail";
import CircledIcon from "../../../components/CircledIcon";
import useAuth from "../../../components/Authentication/useAuth";
import ModalExtendConfirm from "./components/ModalExtendConfirm";
import ModalVerifyTransfer from "./components/ModalVerifyTransfer";
import SearchInput from "../../../components/SearchInput";
import ModalViewFile from "./components/ModalViewFile";
import ModalCloseConfirm from "./components/ModalCloseConfirm";
import { byteStringToFileSize, toStringDate } from "../../../utils/common";
import pngIcon from "../../../assets/images/png_icon.png";
import jpgIcon from "../../../assets/images/jpg_icon.png";
import pdfIcon from "../../../assets/icons/pdf_file.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download_icon.svg";
import { ViewIcon } from "../../../assets/icons";
import { CustomDatePicker, DatePickerWrapper } from "../../../components/CustomDatePicker.style";
import { ActionSection } from "../../../components/Filter/Filter.style";

const { danger, gray, primary } = BlueBirdTheme.colors;

const receiptTypeOpts = [
  { label: "Lihat semua", value: "" },
  { label: "Belum ada ijir", value: "BELUM ADA IJIR" },
  { label: "Ijir", value: "IJIR" },
  { label: "Verifikasi bon", value: "VERIFIKASI" },
  { label: "Close bon", value: "CLOSE" },
];

const receiptExpiryOpts = [
  { label: "Lihat semua", value: "" },
  { label: "Aktif", value: "N" },
  { label: "Kadaluarsa", value: "Y" },
];

interface CloseReceiptI {
  pageName: "Close-bon-putih-ijiran" | "Verifikasi-bon";
}

const GridWrapper = styled.div<{ $show: boolean }>`
  display: grid;
  grid-template-columns: ${({ $show }) => ($show ? "calc(100% - 460px - 20px) 460px" : "calc(100%) 0")};
  gap: 20px;
  transition: all .5s;
  > div:nth-child(2) {
    display: ${({ $show }) => ($show ? "block" : "none")};
  }
`;

const CloseReceipt = ({ pageName }: CloseReceiptI) => {
  const useCustomHook = useCloseReceipt(pageName);
  const { userAccount } = useAuth();
  const {
    params,
    setParams,
    isLoading,
    searchText,
    setSearchText,
    selectedRows,
    setSelectedRows,
    areaOpts,
    poolOpts,
    segmentOpts,
    dataWhiteReceipt,
    handleDisplayDoc,
    handleDownloadDoc,
  } = useCustomHook;
  const [temporaryMaxDate, setTemporaryMaxDate] = useState<null | Date>();

  const handleChangeComparison = (value: string) => {
    setParams((curr) => {
      const comparisonArray = curr.komparasi ? [...curr.komparasi] : [];
      const idx = comparisonArray.indexOf(value);
      if (idx >= 0) comparisonArray.splice(idx, 1);
      else comparisonArray.push(value);
      return ({ ...curr, komparasi: comparisonArray });
    });
  };

  const handleChangeDate = (dates: (Date | null)[] | Date | null) => {
    if (Array.isArray(dates) && dates[0] !== null && dates[1] === null) {
      setTemporaryMaxDate(
        addDays(
          dates[0],
          Math.min(differenceInDays(new Date(), dates[0]), 6),
        ),
      );
    } else if (Array.isArray(dates) && dates[1] !== null) {
      setTemporaryMaxDate(new Date());
    }
  };

  const iconByExtension = (extension?: string) => {
    const icon = extension ? {
      pdf: pdfIcon,
      png: pngIcon,
      jpg: jpgIcon,
      jpeg: jpgIcon,
    }[extension] : pngIcon;
    return <img src={icon} width={24} height={24} />;
  };

  const transferProofCard = (
    (dataWhiteReceipt && dataWhiteReceipt.detail_history_closebm.file_receipt)
    && <div style={{
      display: "flex",
      gap: 16,
      flexDirection: "column",
      padding: "12px 16px",
      alignItems: "stretch",
      borderRadius: "4px",
      border: `1px solid ${gray.line}`,
      color: gray.subtitle,
    }}>
      <div style={{
        display: "flex",
        gap: 16,
        alignItems: "center",
      }}>
        <div>
          <Typography level={4} color={gray.subtitle} fontWeight="bold">Bukti transfer</Typography>
          <br />
          <Typography level={5} color={gray.subtitle}>
            Ditransfer pada : {
              toStringDate(dataWhiteReceipt.detail_history_closebm.transfer_date)
            }
          </Typography>
        </div>
      </div>
      <div style={{
        display: "flex",
        gap: 16,
        padding: "12px 16px",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "4px",
        border: `1px solid ${gray.line}`,
        color: gray.subtitle,
      }}>
        <div style={{
          display: "flex",
          gap: 16,
          alignItems: "center",
        }}>
          {iconByExtension(dataWhiteReceipt.detail_history_closebm.file_receipt.file_name.split(".").pop())}
          <div>
            <Typography level={6}>
              {dataWhiteReceipt.detail_history_closebm.file_receipt.file_name}
            </Typography>
            <br />
            <Typography level={6}>
              {byteStringToFileSize(
                dataWhiteReceipt.detail_history_closebm.file_receipt.file_size,
              )}
            </Typography>
          </div>
        </div>
        <div style={{
          display: "flex",
          gap: 16,
        }}>
          <Button
            backgroundColor={primary[100]}
            onClick={handleDisplayDoc}
          >
            <ViewIcon color={gray.subtitle} />
          </Button>
          <Button
            backgroundColor={primary[100]}
            onClick={handleDownloadDoc}
          >
            <DownloadIcon color={gray.subtitle} />
          </Button>
        </div>
      </div>
    </div>
  );

  return (<>
    <PageWrapper>
      <HeaderText>
        <Typography level={3} color={gray.title} fontWeight="medium" >
          {pageName.replaceAll("-", " ")}
        </Typography>
      </HeaderText>
      <GridWrapper $show={(selectedRows && selectedRows.length > 0) || pageName === "Close-bon-putih-ijiran"}>
        <PageCard>
          <CardContent style={{ gap: 16, padding: 16 }}>
            <ActionSection>
              <div style={{
                display: "flex", gap: 9, fontWeight: 500, fill: danger[200],
              }}>
                <CircledIcon $bgColor={danger[500]} >
                  <PaperListIcon color="white" />
                </CircledIcon>
                <Typography level={4} fontWeight="medium" color={gray.subtitle}>Bon merah</Typography>
              </div>
              {pageName === "Close-bon-putih-ijiran"
                && < div style={{ display: "flex" }}>
                  <Checkbox label="Pas" value="Pas" onChange={(e) => handleChangeComparison(e.target.value)} checked={params.komparasi?.includes("Pas")} />
                  <Checkbox label="Lebih" value="Lebih" onChange={(e) => handleChangeComparison(e.target.value)} checked={params.komparasi?.includes("Lebih")} />
                  <Checkbox label="Kurang" value="Kurang" onChange={(e) => handleChangeComparison(e.target.value)} checked={params.komparasi?.includes("Kurang")} />
                </div>
              }
            </ActionSection>
            <ActionSection style={{ justifyContent: "unset" }}>
              <SearchInput
                placeholder="Cari data"
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
              />
              {pageName === "Verifikasi-bon"
                && <DatePickerWrapper>
                  <CustomDatePicker
                    variant="withRange"
                    label="Tanggal pengajuan"
                    startDate={params.dates[0]}
                    endDate={params.dates[1]}
                    onChange={handleChangeDate}
                    onReset={() => {
                      setTemporaryMaxDate(new Date());
                      setParams((curr) => ({ ...curr, dates: [new Date(), new Date()] }));
                    }}
                    onSubmit={(dates: Date[] | null) => {
                      setTemporaryMaxDate(new Date());
                      setParams((curr) => ({ ...curr, dates: dates || [new Date(), new Date()] }));
                    }}
                    maxDate={temporaryMaxDate || new Date()}
                  />
                </DatePickerWrapper>
              }
              <FilterDropdown
                id="area"
                options={areaOpts}
                label="Area"
                value={params.area?.label || ""}
                onSelected={
                  (opt) => {
                    setParams((curr) => ({
                      ...curr,
                      area: opt as { label: string, value: string },
                      pool: undefined,
                      segment: undefined,
                    }));
                  }
                }
              />
              <FilterDropdown
                id="pool"
                options={poolOpts}
                label="Pool"
                value={params.pool?.label || ""}
                onSelected={
                  (opt) => {
                    setParams((curr) => ({
                      ...curr,
                      pool: opt as { label: string, value: string },
                      segment: undefined,
                    }));
                  }
                }
              />
              <FilterDropdown
                id="segment"
                options={segmentOpts.length > 0
                  ? [{ label: "Lihat semua", value: "" }, ...segmentOpts]
                  : []
                }
                label="Segmen"
                value={params.segment?.label || ""}
                onSelected={
                  (opt) => {
                    setParams((curr) => ({
                      ...curr,
                      segment: opt as { label: string, value: string },
                    }));
                  }
                }
              />
              {pageName === "Close-bon-putih-ijiran"
                && <>
                  <FilterDropdown
                    id="receiptType"
                    options={receiptTypeOpts}
                    label="Status bon"
                    value={params.receiptType?.label}
                    onSelected={
                      (opt) => {
                        setParams((curr) => ({
                          ...curr,
                          receiptType: opt as { label: string, value: string },
                        }));
                      }
                    } />
                  <FilterDropdown
                    id="receiptExpiry"
                    width="150px"
                    options={receiptExpiryOpts}
                    label="Status masa bon"
                    value={params.receiptExpiry?.label}
                    onSelected={
                      (opt) => {
                        setParams((curr) => ({
                          ...curr,
                          receiptExpiry: opt as { label: string, value: string },
                        }));
                      }
                    }
                  />
                </>
              }
            </ActionSection>
            <RedReceiptTable useCustomHook={useCustomHook} />
          </CardContent>
        </PageCard>
        <PageCard>
          <CardContent style={{ gap: 16, padding: 16 }}>
            <div style={{ display: "flex", gap: 9, justifyContent: "space-between" }}>
              <div style={{
                display: "flex", gap: 9, fontWeight: 500, fill: danger[200],
              }}>
                <CircledIcon $bgColor="#8A93A7" >
                  <PaperListIcon color="white" />
                </CircledIcon>
                <Typography level={4} fontWeight="medium" color={gray.subtitle}>Bon putih ijiran</Typography>
              </div>
              {pageName === "Verifikasi-bon"
                && <Button
                  variant="text"
                  size="sm"
                  onClick={() => {
                    setSelectedRows([]);
                  }}
                >
                  <CrossIcon height="20px" width="20px" />
                </Button>
              }
            </div>
            <WhiteReceiptContent
              useCustomHook={useCustomHook}
              transferProofCard={transferProofCard}
            />
          </CardContent>
        </PageCard>
      </GridWrapper>
    </PageWrapper >
    {isLoading && (
      <PageLoading />
    )}
    <ModalReceiptDetail useCustomHook={useCustomHook} />
    <ModalExtendConfirm useCustomHook={useCustomHook} />
    <ModalVerifyTransfer useCustomHook={useCustomHook} />
    <ModalRedReceiptDetail useCustomHook={useCustomHook} />
    <ModalCloseConfirm useCustomHook={useCustomHook} transferProofCard={transferProofCard} />

    <ModalViewFile useCustomHook={useCustomHook} />
  </>);
};

export default CloseReceipt;
